import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, TriangleAlert } from 'lucide-react';

import type { CreateStatusBody, UpdateStatusBody, Workflow } from '@tasks/contracts';

import {
  createStatus,
  createTransition,
  deleteStatus,
  deleteTransition,
  getWorkflowById,
  listWorkflows,
  reorderStatuses,
  updateStatus,
  updateTransition,
} from '../../api/workflow';
import { listMembers } from '../../api/projects';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Input } from '../../components/Input';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { StatusRow } from './StatusRow';
import { TransitionMatrix } from './TransitionMatrix';
import { WorkflowSelector } from './WorkflowSelector';
import styles from './WorkflowSettingsPage.module.css';

/** Настройки workflow: статусы (WIP, категория) и матрица переходов (UC-PRJ-06/07). */
export function WorkflowSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [newStatusName, setNewStatusName] = useState('');
  const [statusToDelete, setStatusToDelete] = useState<string | null>(null);
  // Выбранный набор: по умолчанию — workflow проекта по умолчанию. Правки
  // статусов и переходов идут в него, а не в «первый попавшийся».
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const workflowsQuery = useQuery({
    queryKey: queryKeys.projects.workflows(key),
    queryFn: ({ signal }) => listWorkflows(key, signal),
    enabled: key.length > 0,
  });

  const workflows = workflowsQuery.data ?? [];
  const effectiveId =
    selectedId ?? workflows.find((item) => item.isDefault)?.id ?? workflows[0]?.id ?? null;

  const workflowQuery = useQuery({
    queryKey:
      effectiveId === null
        ? queryKeys.projects.workflow(key)
        : queryKeys.projects.workflowById(key, effectiveId),
    queryFn: ({ signal }) =>
      effectiveId === null
        ? Promise.reject(new Error('В проекте нет workflow'))
        : getWorkflowById(key, effectiveId, signal),
    enabled: key.length > 0 && effectiveId !== null,
  });

  const workflow: Workflow | null = workflowQuery.data ?? null;
  const deleteTargetName =
    workflow?.statuses.find((status) => status.id === statusToDelete)?.name ?? '';

  const membersQuery = useQuery({
    queryKey: queryKeys.projects.members(key),
    queryFn: ({ signal }) => listMembers(key, signal),
    enabled: key.length > 0,
  });
  const members = membersQuery.data ?? [];

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.workflow(key) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.workflows(key) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(key) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(key) });
  };

  const onError = (cause: unknown) => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const createStatusMutation = useMutation({
    mutationFn: (body: CreateStatusBody) =>
      createStatus(key, {
        ...body,
        // Новый статус появляется в выбранном наборе, а не в дефолтном:
        // иначе на экране одного workflow менялся бы другой.
        ...(effectiveId === null ? {} : { workflowId: effectiveId }),
      }),
    onSuccess: () => {
      setNewStatusName('');
      invalidate();
    },
    onError,
  });

  const updateStatusMutation = useMutation({
    mutationFn: (variables: { readonly statusId: string; readonly patch: UpdateStatusBody }) =>
      updateStatus(key, variables.statusId, variables.patch),
    onSuccess: invalidate,
    onError,
  });

  const deleteStatusMutation = useMutation({
    mutationFn: (statusId: string) => deleteStatus(key, statusId),
    onSuccess: () => {
      setStatusToDelete(null);
      invalidate();
    },
    onError,
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: readonly string[]) => reorderStatuses(key, { ids: [...ids] }),
    onSuccess: invalidate,
    onError,
  });

  const toggleTransitionMutation = useMutation({
    mutationFn: async (variables: {
      readonly fromStatusId: string;
      readonly toStatusId: string;
      readonly name: string;
      readonly existingId: string | null;
    }) => {
      if (variables.existingId) {
        await deleteTransition(key, variables.existingId);
        return;
      }
      await createTransition(key, {
        fromStatusId: variables.fromStatusId,
        toStatusId: variables.toStatusId,
        name: variables.name,
        onlyAssignee: false,
        requireResolution: false,
        ...(effectiveId === null ? {} : { workflowId: effectiveId }),
      });
    },
    onSuccess: invalidate,
    onError,
  });

  const updateTransitionMutation = useMutation({
    mutationFn: (variables: {
      readonly transitionId: string;
      readonly patch: { readonly onlyAssignee?: boolean; readonly requireResolution?: boolean };
    }) => updateTransition(key, variables.transitionId, variables.patch),
    onSuccess: invalidate,
    onError,
  });

  if (workflowsQuery.isPending || (effectiveId !== null && workflowQuery.isPending)) {
    return <Skeleton variant="row" count={6} />;
  }

  if (!workflow || effectiveId === null) {
    return <p className={styles['hint']}>Не удалось загрузить workflow проекта.</p>;
  }

  const move = (statusId: string, direction: -1 | 1) => {
    const ids = workflow.statuses.map((status) => status.id);
    const index = ids.indexOf(statusId);
    const target = index + direction;
    const moved = ids[index];
    const swapped = ids[target];
    if (index < 0 || target < 0 || target >= ids.length || !moved || !swapped) {
      return;
    }
    const next = [...ids];
    next[index] = swapped;
    next[target] = moved;
    reorderMutation.mutate(next);
  };

  return (
    <div className={styles['page']}>
      <WorkflowSelector
        projectKey={key}
        workflows={workflows}
        selectedId={effectiveId}
        isLoading={workflowsQuery.isPending}
        onSelect={setSelectedId}
        onChanged={invalidate}
      />

      {workflow.problems.length > 0 ? (
        <div className={styles['problems']} role="alert">
          <TriangleAlert aria-hidden size={16} />
          <ul>
            {workflow.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <section className={styles['block']} aria-label="Статусы">
        <h2>Статусы</h2>
        <table className={styles['statusTable']}>
          <thead>
            <tr>
              <th scope="col">Название</th>
              <th scope="col">Категория</th>
              <th scope="col">WIP-лимит</th>
              <th scope="col">Ответственный по умолчанию</th>
              <th scope="col">Порядок</th>
              <th scope="col">
                <span className="u-visually-hidden">Действия</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {workflow.statuses.map((status, index) => (
              <StatusRow
                key={status.id}
                status={status}
                isFirst={index === 0}
                isLast={index === workflow.statuses.length - 1}
                members={members.map((member) => member.user)}
                onRename={(name) => updateStatusMutation.mutate({ statusId: status.id, patch: { name } })}
                onCategory={(category) => updateStatusMutation.mutate({ statusId: status.id, patch: { category } })}
                onWipLimit={(wipLimit) => updateStatusMutation.mutate({ statusId: status.id, patch: { wipLimit } })}
                onDefaultAssignee={(defaultAssigneeId) =>
                  updateStatusMutation.mutate({ statusId: status.id, patch: { defaultAssigneeId } })
                }
                onMove={(direction) => move(status.id, direction)}
                onDelete={() => setStatusToDelete(status.id)}
              />
            ))}
          </tbody>
        </table>

        <div className={styles['addRow']}>
          <Input
            label="Новый статус"
            value={newStatusName}
            placeholder="Например, На проверке"
            onChange={(event) => setNewStatusName(event.target.value)}
          />
          <Button
            variant="secondary"
            icon={<Plus size={14} />}
            loading={createStatusMutation.isPending}
            disabled={newStatusName.trim().length === 0}
            onClick={() => createStatusMutation.mutate({ name: newStatusName.trim(), category: 'todo', wipLimit: null })}
          >
            Добавить статус
          </Button>
        </div>
      </section>

      <section className={styles['block']} aria-label="Матрица переходов">
        <h2>Переходы</h2>
        <p className={styles['hint']}>
          Отметьте допустимые переходы между статусами. «исп.» — переход доступен только исполнителю,
          «рез.» — при переходе обязательно указывать решение.
        </p>
        <p className={styles['hint']}>
          «Ответственный по умолчанию» у статуса меняет исполнителя задачи при переходе в этот
          статус. Значение «Не менять» оставляет прежнего ответственного.
        </p>
        <TransitionMatrix
          workflow={workflow}
          onToggle={(from, to, existing) =>
            toggleTransitionMutation.mutate({
              fromStatusId: from.id,
              toStatusId: to.id,
              name: `${from.name} → ${to.name}`,
              existingId: existing?.id ?? null,
            })
          }
          onCondition={(transition, patch) =>
            updateTransitionMutation.mutate({ transitionId: transition.id, patch })
          }
        />
      </section>

      <ConfirmDialog
        open={statusToDelete !== null}
        title={`Удалить статус «${deleteTargetName}»?`}
        description="Статус можно удалить, только если в нём нет задач."
        confirmLabel="Удалить"
        isPending={deleteStatusMutation.isPending}
        onConfirm={() => {
          if (statusToDelete) {
            deleteStatusMutation.mutate(statusToDelete);
          }
        }}
        onClose={() => setStatusToDelete(null)}
      />
    </div>
  );
}
